/* ══════════════════════════════════════════════════════════
   AUDIO RECORDER SURFACE — shared recording surface
   Used by:
     - AudioCapture (standalone audio task, multi-clip + notes flow)
     - NarrateStep   (signification audio sub-step, single-clip)
   Visual language: dark editorial. Georgia-italic prompt, coral accents,
   big central action circle, FFT-driven waveform body, dedicated pause
   button when recording (per design decision — discoverability over the
   tap-vs-hold gesture used previously).
   ══════════════════════════════════════════════════════════ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './AudioRecorderSurface.css';
import { AudioClip, AudioRecorderErrorName } from './useAudioRecorder';

export type AudioSurfaceState = 'ready' | 'recording' | 'paused' | 'review';

export interface AudioRecorderSurfaceProps {
  state: AudioSurfaceState;
  prompt: string;
  /** Live recording duration in seconds (the in-progress clip). */
  duration: number;
  /** 0..1 microphone level for the live waveform pulse. */
  audioLevel: number;
  /** Per-bin FFT magnitudes (0..255), live during recording. The surface
      slices this to drive a true spectrum-style bar visualisation; when
      not recording, the last frame is held as a frozen snapshot. */
  frequencyData?: Uint8Array | null;
  /** All clips captured so far. Latest is `clips[clips.length - 1]`. */
  clips: AudioClip[];
  /** False for NarrateStep (single clip); true for AudioCapture (multi). */
  allowMultipleClips?: boolean;

  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  /** Stop the recorder. Pass `{ discard: true }` to drop the recording
      without producing a clip (used for the min-duration guard). */
  onStop: (opts?: { discard?: boolean }) => void;
  onDeleteClip: (clipId: string) => void;
  /** Advance past review (e.g. to notes screen, or back to caller). */
  onContinue: () => void;
  /** Multi-clip only: tapped "+ Record another" — should transition to
      the ready state (with clips persisted), NOT begin recording.
      User then taps the central mic to actually start the new clip. */
  onRecordAnother?: () => void;

  /** Recorder error (mic permission, no device, etc.). When set, the
      surface body renders an error block with a retry button instead
      of the normal recording UI. */
  error?: { name: AudioRecorderErrorName; message: string } | null;
  /** Called by the retry button in the error block. Typically should
      clear the error and re-attempt `startRecording`. */
  onRetry?: () => void;
  /** Minimum clip length, in seconds. Tapping stop before this elapses
      discards the clip and shows a transient "Too short" toast.
      Default: 2. */
  minDurationSec?: number;
  /** Maximum clip length, in seconds. The surface shows a countdown in
      the status row 30s before the cap and auto-stops at the cap.
      Default: 300 (5 minutes). */
  maxDurationSec?: number;
  /** True when the recorder pause came from a visibilitychange (app
      backgrounded). Surface renders a hint so the user knows they need
      to tap resume manually. */
  wasAutoPaused?: boolean;

  /** Top-left slot for caller-specific affordance (close button, back arrow). */
  headerLeft?: React.ReactNode;
  /** Label for the primary continue button. Defaults to "Continue". */
  continueLabel?: string;
}

/** Number of bars in the spectrum waveform. Each bar is one frequency band.
    Bins 1..NUM_BARS skipped DC (bin 0). At fftSize=256 and 48 kHz, each bin
    is ~187 Hz wide; 24 bars covers ~187 Hz – 4.5 kHz, the voice band. */
const NUM_BARS = 24;
const MAX_BAR_HEIGHT = 64;
const MIN_BAR_HEIGHT = 4;

function formatTime(sec: number): string {
  const s = Math.floor(sec);
  return `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
}

const MicIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
  </svg>
);
const StopSquare = () => <span className="ars-stop-square" />;
const PlayIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="#fff">
    <polygon points="7,4 20,12 7,20" />
  </svg>
);
const PauseIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="#fff">
    <rect x="6" y="4" width="4" height="16" />
    <rect x="14" y="4" width="4" height="16" />
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

export const AudioRecorderSurface: React.FC<AudioRecorderSurfaceProps> = ({
  state,
  prompt,
  duration,
  audioLevel: _audioLevel,
  frequencyData,
  clips,
  allowMultipleClips = true,
  onStart,
  onPause,
  onResume,
  onStop,
  onDeleteClip,
  onContinue,
  onRecordAnother,
  error = null,
  onRetry,
  minDurationSec = 2,
  maxDurationSec = 300,
  wasAutoPaused = false,
  headerLeft,
  continueLabel = 'Continue',
}) => {
  // Playback for review/ready states with clips.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingClipId, setPlayingClipId] = useState<string | null>(null);

  // Transient toast surfaced when the user stops too quickly (clip discarded).
  const [tooShortToast, setTooShortToast] = useState(false);
  const tooShortTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hold the last live FFT snapshot so paused/review/ready states can show a
  // frozen visualization (the captured energy from when the user was speaking)
  // rather than reverting to a fake decorative pattern.
  const lastFreqRef = useRef<Uint8Array | null>(null);
  useEffect(() => {
    if (frequencyData && state === 'recording') {
      lastFreqRef.current = frequencyData;
    }
  }, [frequencyData, state]);

  // Max-duration auto-stop. Fires once when duration crosses the cap.
  // `onStop` is idempotent in the hook (guards on MediaRecorder state).
  useEffect(() => {
    if (state === 'recording' && duration >= maxDurationSec) {
      onStop();
    }
  }, [state, duration, maxDurationSec, onStop]);

  // Cleanup toast timer on unmount.
  useEffect(() => {
    return () => {
      if (tooShortTimer.current) clearTimeout(tooShortTimer.current);
    };
  }, []);

  // Bars shown by the visualization. During recording, drive from the live
  // frequencyData. Otherwise, use the frozen last snapshot. If neither exists
  // (first mount, nothing recorded yet), bars sit at their minimum height —
  // an honest "no signal" baseline rather than fake activity.
  const barHeights = useMemo(() => {
    const source = state === 'recording' ? frequencyData : lastFreqRef.current;
    const heights = new Array<number>(NUM_BARS);
    if (!source) {
      for (let i = 0; i < NUM_BARS; i++) heights[i] = MIN_BAR_HEIGHT;
      return heights;
    }
    for (let i = 0; i < NUM_BARS; i++) {
      // Skip bin 0 (DC); take bins 1..NUM_BARS for the voice-relevant band.
      const v = source[i + 1] ?? 0;
      const norm = v / 255;
      heights[i] = MIN_BAR_HEIGHT + norm * (MAX_BAR_HEIGHT - MIN_BAR_HEIGHT);
    }
    return heights;
  }, [state, frequencyData]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  const togglePlay = (clip: AudioClip) => {
    if (!audioRef.current) return;
    if (playingClipId === clip.id) {
      audioRef.current.pause();
      setPlayingClipId(null);
    } else {
      audioRef.current.src = clip.url;
      audioRef.current.play().catch(() => { /* ignore — user gesture races */ });
      setPlayingClipId(clip.id);
    }
  };

  const latestClip = clips[clips.length - 1] ?? null;
  const totalDuration = clips.reduce((sum, c) => sum + c.duration, 0);

  // Seconds left before the max-duration auto-stop. Used both for the
  // countdown in the status row and to colour the status as a warning.
  const remainingSec = Math.max(0, maxDurationSec - duration);
  const inCountdownZone = state === 'recording' && remainingSec <= 30;

  // ── Status text per state ──
  let statusText = 'READY';
  if (state === 'recording') {
    statusText = inCountdownZone
      ? `RECORDING · ${formatTime(duration)} · STOPS IN ${remainingSec}s`
      : `RECORDING · ${formatTime(duration)}`;
  } else if (state === 'paused') statusText = `PAUSED · ${formatTime(duration)}`;
  else if (state === 'review') {
    statusText = allowMultipleClips && clips.length > 1
      ? `${clips.length} CLIPS · ${formatTime(totalDuration)} TOTAL`
      : `RECORDED · ${formatTime(latestClip?.duration ?? 0)}`;
  } else if (state === 'ready' && clips.length > 0) {
    statusText = `${clips.length} CLIP${clips.length > 1 ? 'S' : ''} · ${formatTime(totalDuration)}`;
  }

  /** Wraps the stop callback to enforce the minimum-duration guard.
      Below the floor → fire discard + transient toast; otherwise normal stop. */
  const handleStopTap = () => {
    if (duration < minDurationSec) {
      onStop({ discard: true });
      setTooShortToast(true);
      if (tooShortTimer.current) clearTimeout(tooShortTimer.current);
      tooShortTimer.current = setTimeout(() => setTooShortToast(false), 2500);
      return;
    }
    onStop();
  };

  // ── Action button content per state ──
  const isPlayingLatest = !!latestClip && playingClipId === latestClip.id;
  let mainAction: React.ReactNode;
  let mainAriaLabel = '';
  let mainOnClick: () => void = () => {};

  if (state === 'ready') {
    mainAction = <MicIcon />;
    mainAriaLabel = clips.length > 0 ? 'Record another clip' : 'Start recording';
    mainOnClick = onStart;
  } else if (state === 'recording') {
    mainAction = <StopSquare />;
    mainAriaLabel = 'Stop recording';
    mainOnClick = handleStopTap;
  } else if (state === 'paused') {
    mainAction = <PlayIcon />;
    mainAriaLabel = 'Resume recording';
    mainOnClick = onResume;
  } else if (state === 'review') {
    mainAction = isPlayingLatest ? <PauseIcon /> : <PlayIcon />;
    mainAriaLabel = isPlayingLatest ? 'Pause playback' : 'Play recording';
    mainOnClick = () => latestClip && togglePlay(latestClip);
  }

  // ── Hint text per state ──
  let hint = '';
  if (state === 'ready') hint = clips.length > 0 ? 'Tap to record another' : 'Tap to start recording';
  else if (state === 'recording') hint = 'Tap square to stop';
  else if (state === 'paused') hint = 'Tap to resume · Stop to save this clip';
  else if (state === 'review') hint = isPlayingLatest ? 'Playing latest clip…' : 'Tap to play your recording';

  // ── Spectrum waveform — true FFT bins, one bar per band ──
  const renderWaveBars = () => {
    return barHeights.map((h, i) => (
      <span key={i} className="ars-wave-bar" style={{ height: h }} />
    ));
  };

  // Clip chips visible when:
  //   - multi-clip allowed AND clips exist AND not actively recording
  //   - NOT in the error state (error block owns the body)
  const showClipChips = allowMultipleClips && clips.length > 0
    && state !== 'recording' && state !== 'paused' && !error;

  // Error renders in place of the body + action row. State is implicitly
  // 'ready' (errors only arise from getUserMedia at start time).
  const showErrorBlock = !!error;

  return (
    <div className="ars-dark ars-fade-in">
      {headerLeft}

      <div className={`ars-status ${inCountdownZone ? 'ars-status--warning' : ''}`}>{statusText}</div>

      <div className="ars-prompt">
        <span className="ars-prompt-q">&ldquo;</span>{prompt}<span className="ars-prompt-q">&rdquo;</span>
      </div>

      {showErrorBlock ? (
        <div className="ars-error" role="alert">
          <div className="ars-error-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="1" y1="1" x2="23" y2="23" />
              <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
              <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
              <line x1="12" y1="19" x2="12" y2="23" />
            </svg>
          </div>
          <div className="ars-error-msg">{error.message}</div>
          <button
            className="ars-btn-ember ars-error-retry"
            onClick={onRetry ?? onStart}
            type="button"
          >
            {error.name === 'NotAllowedError' ? 'Grant microphone access' : 'Try again'}
          </button>
        </div>
      ) : (
        <>
          <div className="ars-body">
            <div className={`ars-wave ars-wave--${state}`}>{renderWaveBars()}</div>
          </div>

          {wasAutoPaused && state === 'paused' && (
            <div className="ars-autopause-note" role="status">
              Paused while you were away — tap to resume.
            </div>
          )}

          <div className="ars-action-row">
            {state === 'recording' && (
              <button
                className="ars-btn-aux"
                onClick={onPause}
                aria-label="Pause recording"
                type="button"
              >
                <PauseIcon />
              </button>
            )}

            <button
              className="ars-circle"
              onClick={mainOnClick}
              aria-label={mainAriaLabel}
              type="button"
            >
              {mainAction}
            </button>

            {state === 'paused' && (
              <button
                className="ars-btn-aux"
                onClick={handleStopTap}
                aria-label="Stop and save clip"
                type="button"
              >
                <StopSquare />
              </button>
            )}
          </div>
        </>
      )}

      {/* Multi-clip chip list. One row per clip with play/delete. */}
      {showClipChips && (
        <div className="ars-clips" role="list">
          {clips.map((clip, idx) => {
            const isPlayingThis = playingClipId === clip.id;
            return (
              <div key={clip.id} className="ars-clip" role="listitem">
                <button
                  className="ars-clip-play"
                  onClick={() => togglePlay(clip)}
                  aria-label={isPlayingThis ? `Pause clip ${idx + 1}` : `Play clip ${idx + 1}`}
                  type="button"
                >
                  {isPlayingThis ? <PauseIcon /> : <PlayIcon />}
                </button>
                <span className="ars-clip-meta">Clip {idx + 1} · {formatTime(clip.duration)}</span>
                <button
                  className="ars-clip-del"
                  onClick={() => onDeleteClip(clip.id)}
                  aria-label={`Delete clip ${idx + 1}`}
                  type="button"
                >
                  <TrashIcon />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="ars-footer">
        {/* Hint only during recording / paused / first-time-ready. */}
        {(state === 'recording' || state === 'paused' || (state === 'ready' && clips.length === 0)) && (
          <div className="ars-hint">{hint}</div>
        )}

        {/* Footer actions:
              review (single-clip) → Re-record (delete + back to ready) + Continue
              review (multi-clip)  → + Record another + Continue
              ready w/ clips       → just Continue (central mic IS the record-another) */}
        {state === 'review' && (
          <div className="ars-review-actions">
            {!allowMultipleClips && latestClip && (
              <button
                className="ars-btn-ghost"
                onClick={() => onDeleteClip(latestClip.id)}
                type="button"
              >
                Re-record
              </button>
            )}
            {allowMultipleClips && (
              <button
                className="ars-btn-ghost"
                onClick={onRecordAnother ?? onStart}
                type="button"
              >
                + Record another
              </button>
            )}
            <button
              className="ars-btn-ember"
              onClick={onContinue}
              disabled={clips.length === 0}
              type="button"
            >
              {continueLabel}
            </button>
          </div>
        )}

        {state === 'ready' && clips.length > 0 && (
          <div className="ars-review-actions">
            <button
              className="ars-btn-ember"
              onClick={onContinue}
              type="button"
            >
              {continueLabel}
            </button>
          </div>
        )}
      </div>

      {tooShortToast && (
        <div className="ars-toast" role="status" aria-live="polite">
          Too short — try again.
        </div>
      )}

      <audio ref={audioRef} onEnded={() => setPlayingClipId(null)} style={{ display: 'none' }} />
    </div>
  );
};
